// --- The host's one speaking queue ---
//
// Everything the daemon says aloud waits here, one line at a time: a
// spoken answer, task narration, a talk, a lesson. Lines play strictly in
// order; each is synthesised the moment it is queued, so its audio is
// usually ready before the line ahead of it ends.
//
// The listener comes first: pause() (the door opening) cuts the line
// playing and holds the queue; resume() plays that line again from the
// start, then the rest. A held queue resumes by itself after `holdMs`, so
// a door that never closes cannot silence the host for good. stop() drops
// everything. Every change is reported through `events.onChange`.
//
// While the floor is held (floor.ts) the queue is reserved: only the
// holder's own lines play; every other line waits and plays once the
// floor is free, except narration, which would be stale by then.

import type { ChildProcess } from "child_process"
import { unlinkSync } from "fs"
import { elevenLabsSynth, speakLimitMs, systemPlay, type Play, type SpeechEndReason, type SpeechEvents, type Synth, type Utterance, type VoiceRef } from "./speaker"

export type SpeechKind = "answer" | "narration" | "talk" | "lesson" | "line"

export interface QueueItem {
  id: string
  agentId: string | null
  kind: SpeechKind
  text: string
  voice: VoiceRef
  enqueuedAt: number
}

export interface QueueView {
  paused: boolean
  /** The floor is held: only its holder's lines play (floor.ts). */
  reserved: boolean
  playing: QueueItem | null
  waiting: QueueItem[]
  /** Lines played in full, newest first, so one can be replayed. */
  recent: QueueItem[]
}

type Audio = { f: string | null } | { e: unknown }

interface Entry {
  item: QueueItem
  u: Utterance
  /** The line as the voice says it: `u` after pronounce. */
  said: Utterance
  audio: Promise<Audio>
  ac: AbortController
  started: boolean
  /** When the play in progress began; 0 while none is. */
  playingSince: number
  done: (played: boolean) => void
}

const RECENT = 10
const RANK = { high: 0, normal: 1, low: 2 } as const

export class SpeechOut {
  private waiting: Entry[] = []
  private current: Entry | null = null
  private playing: ChildProcess | null = null
  private recent: QueueItem[] = []
  private held = false
  private holdTimer: ReturnType<typeof setTimeout> | null = null
  /** The floor's id while it is held: only lines that carry it play. */
  private floor: string | null = null
  /** Bumped whenever the current line changes hands: a player or a
   *  synthesis that finishes under an older run is ignored. */
  private run = 0
  private seq = 0
  /** How the voice says a line's words (voice.pronunciations, #433).
   *  The queue, its events and replays keep the written text. */
  pronounce: (text: string) => string = (text) => text
  /** What else speaks on this host outside the queue (a script's `say`),
   *  or null (#1126). A line waits while it does, up to `othersWaitMs`,
   *  then plays: a speaker that never stops must not silence the host. */
  othersSpeaking?: () => Promise<string | null>
  othersWaitMs = 10 * 60_000
  othersPollMs = 500

  constructor(
    private synth: Synth = elevenLabsSynth(),
    private play: Play = systemPlay,
    public events: SpeechEvents & { onChange?: (view: QueueView) => void } = {},
    private limitMs: (text: string) => number = speakLimitMs,
    private holdMs = 60_000,
  ) {}

  /** True while anything is playing or waiting to play. */
  get busy(): boolean { return !!this.current || this.waiting.length > 0 }

  get paused(): boolean { return this.held }

  /** Queue a line. Resolves true once it has played in full, false if it
   *  was skipped, stopped or failed. */
  say(u: Utterance): Promise<boolean> { return this.enqueue(u).done }

  /** Queue a line and get its item; `front` puts it next in line. */
  enqueue(u: Utterance, front = false): { item: QueueItem; done: Promise<boolean> } {
    const ac = new AbortController()
    const item: QueueItem = {
      id: `s${Date.now().toString(36)}-${++this.seq}`,
      agentId: u.agentId ?? null, kind: u.kind ?? "line", text: u.text, voice: u.voice, enqueuedAt: Date.now(),
    }
    // Narration says what a task is doing now: after the floor it is old news.
    if (item.kind === "narration" && !this.allowed(u)) return { item, done: Promise.resolve(false) }
    let done!: (played: boolean) => void
    const played = new Promise<boolean>((r) => { done = r })
    // The catch keeps an early failure from being reported as unhandled
    // before this line's turn comes.
    const said: Utterance = { ...u, text: this.pronounce(u.text) }
    const audio = this.synth(said, ac.signal).then((f) => ({ f }), (e) => ({ e }))
    const entry: Entry = { item, u, said, audio, ac, started: false, playingSince: 0, done }
    if (front) this.waiting.unshift(entry)
    else {
      // A "high" agent's line goes ahead of waiting normal and low ones,
      // a normal one ahead of low ones; equals keep arrival order.
      const mine = RANK[u.voice.priority ?? "normal"]
      const at = this.waiting.findIndex((e) => RANK[e.u.voice.priority ?? "normal"] > mine)
      if (at < 0) this.waiting.push(entry)
      else this.waiting.splice(at, 0, entry)
    }
    this.changed()
    this.next()
    return { item, done: played }
  }

  view(): QueueView {
    return { paused: this.held, reserved: this.floor !== null, playing: this.current?.item ?? null, waiting: this.waiting.map((e) => e.item), recent: [...this.recent] }
  }

  /** Drop one line, playing or waiting; the rest keep their order. */
  skip(id: string): boolean {
    if (this.current?.item.id === id) {
      this.drop(this.detach("skipped")!)
      this.changed()
      this.next()
      return true
    }
    const i = this.waiting.findIndex((e) => e.item.id === id)
    if (i < 0) return false
    this.drop(this.waiting.splice(i, 1)[0])
    this.changed()
    return true
  }

  /** Move a waiting line to the front: it plays after the current one. */
  front(id: string): boolean {
    if (this.current?.item.id === id) return true
    const i = this.waiting.findIndex((e) => e.item.id === id)
    if (i < 0) return false
    this.waiting.unshift(...this.waiting.splice(i, 1))
    this.changed()
    return true
  }

  /** Say a queued or recently played line again, next in line. */
  replay(id: string): QueueItem | null {
    const found = [this.current?.item, ...this.waiting.map((e) => e.item), ...this.recent].find((i) => i?.id === id)
    if (!found) return null
    return this.enqueue({ voice: found.voice, text: found.text, agentId: found.agentId ?? undefined, kind: found.kind }, true).item
  }

  /** The listener is speaking: cut the current line and hold the queue. */
  pause(): void {
    if (this.holdTimer) clearTimeout(this.holdTimer)
    this.holdTimer = setTimeout(() => this.resume(), this.holdMs)
    this.holdTimer.unref?.()
    if (this.held) return
    this.held = true
    const cut = this.detach("paused")
    if (cut) this.waiting.unshift(cut)
    this.changed()
  }

  /** The listener's turn is over: the cut line plays again, then the rest. */
  resume(): void {
    if (this.holdTimer) clearTimeout(this.holdTimer)
    this.holdTimer = null
    if (!this.held) return
    this.held = false
    this.changed()
    this.next()
  }

  /** The floor is taken: the line playing is cut unless it is the
   *  holder's, and plays again with the rest once the floor is free. */
  reserve(floor: string): void {
    if (this.floor === floor) return
    this.floor = floor
    if (this.current && !this.allowed(this.current.u)) this.waiting.unshift(this.detach("floor")!)
    // Narration already waiting would be old news by the time it plays.
    const stale = this.waiting.filter((w) => w.item.kind === "narration" && !this.allowed(w.u))
    this.waiting = this.waiting.filter((w) => !stale.includes(w))
    for (const e of stale) this.drop(e)
    this.changed()
    this.next()
  }

  /** The floor is free: every line that waited plays, in order. With
   *  `silence`, the floor was taken from its holder: its own lines,
   *  playing or waiting, are dropped. */
  release(silence = false): void {
    if (this.floor === null) return
    const floor = this.floor
    this.floor = null
    if (silence) {
      if (this.current?.u.floor === floor) this.drop(this.detach("stopped")!)
      const theirs = this.waiting.filter((w) => w.u.floor === floor)
      this.waiting = this.waiting.filter((w) => w.u.floor !== floor)
      for (const e of theirs) this.drop(e)
    }
    this.changed()
    this.next()
  }

  private allowed(u: Utterance): boolean { return this.floor === null || u.floor === this.floor }

  /** Drop every line of one kind — a talk or lesson silencing itself. */
  cancel(kind: SpeechKind): void {
    let dropped = false
    if (this.current?.item.kind === kind) { this.drop(this.detach("cancelled")!); dropped = true }
    for (const e of this.waiting.filter((w) => w.item.kind === kind)) { this.drop(e); dropped = true }
    this.waiting = this.waiting.filter((w) => w.item.kind !== kind)
    if (!dropped) return
    this.changed()
    this.next()
  }

  /** Silence now: kill what is playing, drop everything, stop holding. */
  stop(): void {
    const all = [this.detach("stopped"), ...this.waiting.splice(0)].filter((e): e is Entry => !!e)
    for (const e of all) this.drop(e)
    if (this.holdTimer) clearTimeout(this.holdTimer)
    this.holdTimer = null
    const wasHeld = this.held
    this.held = false
    if (all.length || wasHeld) this.changed()
  }

  /** Start the next line that may play, unless one is playing or the
   *  queue is held. */
  private next(): void {
    if (this.held || this.current) return
    const at = this.waiting.findIndex((w) => this.allowed(w.u))
    if (at < 0) return
    const e = this.waiting.splice(at, 1)[0]
    this.current = e
    const run = ++this.run
    this.changed()
    void e.audio.then(async (got) => {
      if (run !== this.run) return
      if ("e" in got) return this.finish(e, false)
      await this.othersDone(run)
      if (run !== this.run) return
      this.playOne(e, got.f, run)
    })
  }

  /** Wait while something outside the queue speaks on this host. */
  private async othersDone(run: number): Promise<void> {
    if (!this.othersSpeaking) return
    const until = Date.now() + this.othersWaitMs
    while (run === this.run && Date.now() < until) {
      let who: string | null = null
      try { who = await this.othersSpeaking() } catch { /* can't tell: play */ }
      if (!who) { this.othersOutlasted = false; return }
      // It outlasted one full wait already: the lines after it do not
      // each wait again, or one endless sound silences the host for good.
      if (this.othersOutlasted) return
      await new Promise((r) => setTimeout(r, this.othersPollMs))
    }
    if (run === this.run) this.othersOutlasted = true
  }
  private othersOutlasted = false

  private playOne(e: Entry, file: string | null, run: number): void {
    const p = this.play(file, e.said)
    this.playing = p
    e.playingSince = Date.now()
    if (!e.started) {
      e.started = true
      this.events.onStart?.(e.u, Date.now())
      e.u.onStart?.()
    }
    let ended = false
    let overran = false
    // A player that never exits (a hung `say`) must not hold every later
    // line: past its bound it is killed and the line fails.
    const watchdog = setTimeout(() => { if (!ended) { overran = true; p.kill() } }, this.limitMs(e.said.text))
    const end = (completed: boolean) => {
      if (ended) return
      ended = true
      clearTimeout(watchdog)
      if (this.playing === p) this.playing = null
      // Paused, skipped or stopped meanwhile: whoever did it owns the line.
      if (run !== this.run) return
      this.events.onEnd?.(e.u, Date.now(), completed)
      this.stopped(e, completed ? "finished" : overran ? "watchdog" : "failed")
      this.finish(e, completed)
    }
    // A player that cannot start (not installed) may never emit close.
    p.on("error", () => end(false))
    p.on("close", (code) => end(code === 0))
  }

  private finish(e: Entry, played: boolean): void {
    this.detach()
    if (played) this.recent = [e.item, ...this.recent].slice(0, RECENT)
    this.drop(e, played)
    this.changed()
    this.next()
  }

  /** Take the current line off the speakers; returns it. `reason` is why,
   *  when it is cut rather than over. */
  private detach(reason?: SpeechEndReason): Entry | null {
    const e = this.current
    if (!e) return null
    this.current = null
    this.run++
    const p = this.playing
    this.playing = null
    p?.kill()
    if (reason) this.stopped(e, reason)
    return e
  }

  /** Report the end of a play in progress, once. */
  private stopped(e: Entry, reason: SpeechEndReason): void {
    if (!e.playingSince) return
    const playedMs = Date.now() - e.playingSince
    e.playingSince = 0
    try { this.events.onStopped?.(e.u, reason, playedMs) } catch { /* a listener's failure is not the queue's */ }
  }

  /** Settle a line for good and delete its audio file. */
  private drop(e: Entry, played = false): void {
    e.ac.abort()
    e.done(played)
    void e.audio.then((got) => {
      if ("f" in got && got.f) try { unlinkSync(got.f) } catch { /* already gone */ }
    })
  }

  private changed(): void {
    try { this.events.onChange?.(this.view()) } catch { /* a listener's failure is not the queue's */ }
  }
}
